import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { ChevronLeft, Camera, CheckCircle2, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { ACTION_TYPES, GRAVITA_META, TYPE_FIELDS } from "../lib/constants";

const POSIZIONI = ["Anteriore destra", "Anteriore sinistra", "Posteriore destra", "Posteriore sinistra", "Motore", "Abitacolo", "Tetto", "Altro"];

export default function ReportForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const tipo = params.get("tipo") || "altro";
  const action = ACTION_TYPES.find((a) => a.key === tipo) || ACTION_TYPES[7];
  const fields = TYPE_FIELDS[tipo] || { posizione: true, descrizione: true };

  const [vehicle, setVehicle] = useState(null);
  const [posizione, setPosizione] = useState("");
  const [descrizione, setDescrizione] = useState("");
  const [gravita, setGravita] = useState("normale");
  const [pinRequired, setPinRequired] = useState(false);
  const [pin, setPin] = useState("");
  const [photoPath, setPhotoPath] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    api.get(`/vehicles/${id}`).then(({ data }) => setVehicle(data)).catch(() => {});
    api.get(`/settings/public?vehicle_id=${id}`).then(({ data }) => { setPinRequired(!!data.pin_required); }).catch(() => {});
  }, [id]);

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setPhotoPreview(URL.createObjectURL(file));
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("vehicle_id", id);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotoPath(data.path);
    } catch (err) {
      toast.error("Errore nel caricamento della foto");
      setPhotoPreview(null);
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (pinRequired && !pin.trim()) {
      toast.error("Inserisci il PIN aziendale per inviare la segnalazione");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/reports", {
        vehicle_id: id,
        tipo,
        posizione,
        descrizione,
        gravita,
        photo_path: photoPath,
        pin: pin.trim(),
      });
      setSuccess(data);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="max-w-md mx-auto min-h-screen flex flex-col items-center justify-center px-6 text-center fade-up">
        <div className="w-20 h-20 rounded-full bg-emerald-500/15 grid place-items-center mb-5">
          <CheckCircle2 className="w-12 h-12 text-emerald-400" />
        </div>
        <h1 className="font-head text-2xl font-bold">Segnalazione inviata!</h1>
        <p className="text-slate-400 mt-2">La tua richiesta e' stata inoltrata all'amministratore.</p>
        <div className="mt-6 w-full bg-slate-900 border border-slate-800 rounded-xl p-4 text-left">
          <div className="flex justify-between text-sm py-1"><span className="text-slate-500">Ticket</span><span className="font-plate font-bold">{success.ticket}</span></div>
          <div className="flex justify-between text-sm py-1"><span className="text-slate-500">Mezzo</span><span className="font-plate">{success.targa}</span></div>
          <div className="flex justify-between text-sm py-1"><span className="text-slate-500">Tipo</span><span>{success.tipo_label}</span></div>
          <div className="flex justify-between text-sm py-1"><span className="text-slate-500">Operatore</span><span>{success.operator_name} {success.operator_surname}</span></div>
        </div>
        <button onClick={() => navigate(`/mezzo/${id}`)} className="mt-6 w-full bg-blue-600 hover:bg-blue-500 py-3 rounded-xl font-semibold" data-testid="back-to-vehicle-btn">Torna al mezzo</button>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto min-h-screen px-4 pt-4 pb-24">
      <header className="flex items-center gap-2 py-2 mb-3">
        <button onClick={() => navigate(`/mezzo/${id}`)} className="p-2 -ml-2 rounded-lg hover:bg-slate-800" data-testid="report-back-btn">
          <ChevronLeft className="w-6 h-6" />
        </button>
        <div>
          <span className="font-head font-semibold block leading-none">{action.label}</span>
          {vehicle && <span className="text-xs text-slate-500 font-plate">{vehicle.targa} · MEZZO {vehicle.n_soc}</span>}
        </div>
      </header>

      <div className="space-y-5">
        {fields.posizione && (
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Posizione</label>
            <input list="posizioni" value={posizione} onChange={(e) => setPosizione(e.target.value)} placeholder="es. Anteriore destra" data-testid="report-posizione-input"
              className="w-full mt-2 bg-slate-900 border border-slate-700 rounded-xl px-3 py-3 text-sm text-slate-100 focus:outline-none focus:border-blue-500" />
            <datalist id="posizioni">{POSIZIONI.map((p) => <option key={p} value={p} />)}</datalist>
          </div>
        )}
        {!fields.posizione && !fields.descrizione && (
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl px-4 py-3 text-sm text-blue-200">
            La richiesta <span className="font-semibold">{action.label}</span> non richiede altri dettagli: compila i tuoi dati e invia.
          </div>
        )}

        {fields.descrizione && (
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Descrizione</label>
            <textarea value={descrizione} onChange={(e) => setDescrizione(e.target.value)} rows={3} placeholder="Descrivi il problema o la richiesta..." data-testid="report-descrizione-input"
              className="w-full mt-2 bg-slate-900 border border-slate-700 rounded-xl px-3 py-3 text-sm text-slate-100 focus:outline-none focus:border-blue-500 resize-none" />
          </div>
        )}

        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Foto (facoltativa)</label>
          <div className="mt-2">
            {photoPreview ? (
              <div className="relative inline-block">
                <img src={photoPreview} alt="anteprima" className="w-28 h-28 object-cover rounded-xl border border-slate-700" />
                <button onClick={() => { setPhotoPreview(null); setPhotoPath(null); }} className="absolute -top-2 -right-2 bg-red-600 rounded-full p-1" data-testid="remove-photo-btn"><X className="w-3 h-3" /></button>
                {uploading && <div className="absolute inset-0 bg-black/60 grid place-items-center rounded-xl"><Loader2 className="w-5 h-5 animate-spin" /></div>}
              </div>
            ) : (
              <label className="flex items-center gap-2 bg-slate-900 border border-dashed border-slate-600 rounded-xl px-4 py-3 text-sm text-slate-400 cursor-pointer hover:border-blue-500 w-fit" data-testid="add-photo-btn">
                <Camera className="w-5 h-5" /> Aggiungi foto
                <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onPhoto} />
              </label>
            )}
          </div>
        </div>

        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Gravita'</label>
          <div className="grid grid-cols-3 gap-2 mt-2">
            {Object.entries(GRAVITA_META).map(([k, m]) => (
              <button key={k} onClick={() => setGravita(k)} data-testid={`report-gravita-${k}`}
                className={`text-xs font-semibold py-2.5 rounded-lg border transition-colors ${gravita === k ? m.active : m.cls}`}>
                {m.label}
              </button>
            ))}
          </div>
        </div>

        {pinRequired ? (
          <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4">
            <label className="text-xs font-semibold uppercase tracking-wider text-blue-400">PIN segnalazioni *</label>
            <input value={pin} onChange={(e) => setPin(e.target.value)} placeholder="Inserisci il PIN fornito dall'azienda" data-testid="operator-pin-input"
              className="w-full mt-1.5 bg-slate-950 border border-slate-700 rounded-lg px-3 py-2.5 text-sm text-slate-100 focus:outline-none focus:border-blue-500 font-plate" />
            <p className="text-[11px] text-slate-500 mt-1.5">Per inviare la segnalazione inserisci il PIN fornito dalla tua azienda.</p>
          </div>
        ) : (
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl px-4 py-3 text-sm text-blue-200" data-testid="no-pin-note">
            Premi "Invia Segnalazione" per inoltrare la richiesta all'amministratore.
          </div>
        )}
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-[#0B0F17]/95 backdrop-blur border-t border-slate-800 p-4">
        <div className="max-w-md mx-auto">
          <button onClick={submit} disabled={submitting} data-testid="report-submit-btn"
            className="w-full bg-blue-600 hover:bg-blue-500 disabled:opacity-60 py-3.5 rounded-xl font-semibold flex items-center justify-center gap-2">
            {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
            Invia Segnalazione
          </button>
        </div>
      </div>
    </div>
  );
}
